import { Component, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { UiAlertService } from '../../../services/ui-alert/ui-alert.service';
import {
  QuickBooksAccount,
  QuickBooksAccountCatalogResponse,
  QuickBooksAccountHint,
  QuickBooksAccountHintResult,
  QuickBooksConnectionState,
  QuickBooksConnectionStatus,
  QuickBooksConnectionsService,
  QuickBooksCustomer,
  QuickBooksCustomerCatalogResponse,
  QuickBooksPartyHint,
  QuickBooksPartyHintResult,
  QuickBooksVendor,
  QuickBooksVendorCatalogResponse,
  WallitAccountCandidate,
  WallitAccountCandidatesResponse,
  WallitAccountEligibilityReason,
  WallitCompanyCandidate,
  WallitCompanyCandidatesResponse,
  WallitSupplierCandidate,
  WallitSupplierCandidatesResponse,
} from '../../../services/quickbooks-connections/quickbooks-connections.service';
import { FINANCE_TABS, FinanceTab } from '../finance-tabs';

interface QuickBooksAccountRow {
  account: QuickBooksAccount;
  depth: number;
}

interface WallitAccountGroup {
  accountTypeCode: WallitAccountCandidate['accountTypeCode'];
  accounts: WallitAccountCandidate[];
  label: string;
}

type ComparisonSection = 'account' | 'vendor' | 'customer';

@Component({
  selector: 'app-finance-connections',
  templateUrl: './finance-connections.component.html',
  styleUrls: ['./finance-connections.component.scss'],
})
export class FinanceConnectionsComponent implements OnInit {
  readonly tabs: FinanceTab[] = FINANCE_TABS;
  readonly stateLabels: Record<QuickBooksConnectionState, string> = {
    AUTHORIZATION_REQUIRES_REPLACEMENT: 'Authorization requires replacement',
    CONFIGURATION_UNAVAILABLE: 'Configuration unavailable',
    CONNECTED_VERIFIED: 'Connected and verified',
    DISCONNECTED: 'Disconnected',
    REMOVED: 'Removed',
    REMOVING: 'Removing',
    REFRESH_RECONCILIATION_REQUIRED: 'Refresh reconciliation required',
    REVOCATION_UNKNOWN: 'Cleanup required',
  };
  readonly eligibilityLabels: Record<WallitAccountEligibilityReason, string> = {
    ACTIVE_NON_ROOT_CHART_ACCOUNT: 'Included in Finance chart',
  };
  readonly hintLabels: Record<QuickBooksAccountHintResult, string> = {
    AMBIGUOUS: 'Review: multiple or unresolved matches',
    CLASSIFICATION_CONFLICT: 'Review: classification conflict',
    EXACT_NAME_AND_CLASSIFICATION: 'Exact name and classification hint',
    INACTIVE_EXACT_NAME: 'Review: inactive exact-name match',
    MISSING: 'No exact-name match',
  };
  readonly partyHintLabels: Record<QuickBooksPartyHintResult, string> = {
    EXACT_NAME: 'Exact normalized display-name hint',
    INACTIVE_EXACT_NAME: 'Review: inactive exact-name result',
    MULTIPLE_EXACT_NAME_MATCHES: 'Review: multiple exact-name results',
    NO_EXACT_NAME: 'No exact display-name result',
  };
  readonly comparisonSectionExpanded: Record<ComparisonSection, boolean> = {
    account: false,
    customer: false,
    vendor: false,
  };
  readonly wallitAccountGroupExpanded: Record<WallitAccountCandidate['accountTypeCode'], boolean> = {
    assets: false,
    expenses: false,
    liabilities: false,
    revenue: false,
  };

  activeTab = FINANCE_TABS.find(tab => tab.id === 'connections') as FinanceTab;
  loading = true;
  starting = false;
  removing = false;
  cleaningUp = false;
  status: QuickBooksConnectionStatus = this.unavailableStatus();
  wallitLoading = true;
  wallitError = false;
  wallitProjection: WallitAccountCandidatesResponse | null = null;
  wallitAccountGroups: WallitAccountGroup[] = [];
  catalogLoading = false;
  catalogError = '';
  accountCatalog: QuickBooksAccountCatalogResponse | null = null;
  quickBooksAccountRows: QuickBooksAccountRow[] = [];
  supplierCandidatesLoading = true;
  supplierCandidatesError = false;
  supplierCandidates: WallitSupplierCandidatesResponse | null = null;
  companyCandidatesLoading = true;
  companyCandidatesError = false;
  companyCandidates: WallitCompanyCandidatesResponse | null = null;
  vendorCatalogLoading = false;
  vendorCatalogError = '';
  vendorCatalog: QuickBooksVendorCatalogResponse | null = null;
  customerCatalogLoading = false;
  customerCatalogError = '';
  customerCatalog: QuickBooksCustomerCatalogResponse | null = null;

  constructor(
    private router: Router,
    private snackBar: MatSnackBar,
    private uiAlertService: UiAlertService,
    private quickBooksConnectionsService: QuickBooksConnectionsService,
  ) {}

  ngOnInit(): void {
    void this.loadStatus();
    this.loadWallitAccountCandidates();
    this.loadWallitSupplierCandidates();
    this.loadWallitCompanyCandidates();
  }

  gotoTab(tab: FinanceTab): void {
    if (tab.link !== this.activeTab.link) {
      void this.router.navigate([tab.link]);
    }
  }

  isComparisonSectionExpanded(section: ComparisonSection): boolean {
    return this.comparisonSectionExpanded[section];
  }

  toggleComparisonSection(section: ComparisonSection): void {
    this.comparisonSectionExpanded[section] = !this.comparisonSectionExpanded[section];
  }

  isWallitAccountGroupExpanded(accountTypeCode: WallitAccountCandidate['accountTypeCode']): boolean {
    return this.wallitAccountGroupExpanded[accountTypeCode];
  }

  toggleWallitAccountGroup(accountTypeCode: WallitAccountCandidate['accountTypeCode']): void {
    this.wallitAccountGroupExpanded[accountTypeCode] = !this.wallitAccountGroupExpanded[accountTypeCode];
  }

  loadStatus(preserveOtherProviderCatalogs = false): Promise<void> {
    this.loading = true;
    return this.quickBooksConnectionsService.getStatus()
      .then(status => {
        this.status = status;
        if (status.state !== 'CONNECTED_VERIFIED' && !preserveOtherProviderCatalogs) {
          this.accountCatalog = null;
          this.quickBooksAccountRows = [];
          this.vendorCatalog = null;
          this.customerCatalog = null;
        }
      })
      .catch(() => {
        this.status = this.unavailableStatus();
        if (!preserveOtherProviderCatalogs) {
          this.accountCatalog = null;
          this.quickBooksAccountRows = [];
          this.vendorCatalog = null;
          this.customerCatalog = null;
        }
      })
      .finally(() => {
        this.loading = false;
      });
  }

  loadWallitAccountCandidates(): void {
    this.wallitLoading = true;
    this.wallitError = false;
    this.quickBooksConnectionsService.getWallitAccountCandidates()
      .then(result => {
        this.wallitProjection = result;
        this.wallitAccountGroups = this.buildWallitAccountGroups(result.accounts);
      })
      .catch(() => {
        this.wallitProjection = null;
        this.wallitAccountGroups = [];
        this.wallitError = true;
      })
      .finally(() => {
        this.wallitLoading = false;
      });
  }

  loadWallitSupplierCandidates(): void {
    this.supplierCandidatesLoading = true;
    this.supplierCandidatesError = false;
    this.quickBooksConnectionsService.getWallitSupplierCandidates()
      .then(result => {
        this.supplierCandidates = result;
      })
      .catch(() => {
        this.supplierCandidates = null;
        this.supplierCandidatesError = true;
      })
      .finally(() => {
        this.supplierCandidatesLoading = false;
      });
  }

  loadWallitCompanyCandidates(): void {
    this.companyCandidatesLoading = true;
    this.companyCandidatesError = false;
    this.quickBooksConnectionsService.getWallitCompanyCandidates()
      .then(result => {
        this.companyCandidates = result;
      })
      .catch(() => {
        this.companyCandidates = null;
        this.companyCandidatesError = true;
      })
      .finally(() => {
        this.companyCandidatesLoading = false;
      });
  }

  loadAccountCatalog(): void {
    if (this.status.state !== 'CONNECTED_VERIFIED' || this.catalogLoading) {
      return;
    }
    this.catalogLoading = true;
    this.catalogError = '';
    this.quickBooksConnectionsService.getAccountCatalog()
      .then(result => {
        this.accountCatalog = result;
        this.quickBooksAccountRows = this.buildQuickBooksAccountRows(result.accounts);
      })
      .catch(error => {
        this.accountCatalog = null;
        this.quickBooksAccountRows = [];
        this.catalogError = this.quickBooksConnectionsService.getSanitizedErrorMessage(
          error,
          'The complete QuickBooks Account catalog could not be loaded.',
        );
        return this.loadStatus();
      })
      .finally(() => {
        this.catalogLoading = false;
      });
  }

  loadVendorCatalog(): void {
    if (this.status.state !== 'CONNECTED_VERIFIED' || this.vendorCatalogLoading) {
      return;
    }
    this.vendorCatalogLoading = true;
    this.vendorCatalogError = '';
    this.quickBooksConnectionsService.getVendorCatalog()
      .then(result => {
        this.vendorCatalog = result;
      })
      .catch(error => {
        this.vendorCatalog = null;
        this.vendorCatalogError = this.quickBooksConnectionsService.getSanitizedErrorMessage(
          error,
          'The complete QuickBooks Vendor catalog could not be loaded.',
        );
        return this.loadStatus(true);
      })
      .finally(() => {
        this.vendorCatalogLoading = false;
      });
  }

  loadCustomerCatalog(): void {
    if (this.status.state !== 'CONNECTED_VERIFIED' || this.customerCatalogLoading) {
      return;
    }
    this.customerCatalogLoading = true;
    this.customerCatalogError = '';
    this.quickBooksConnectionsService.getCustomerCatalog()
      .then(result => {
        this.customerCatalog = result;
      })
      .catch(error => {
        this.customerCatalog = null;
        this.customerCatalogError = this.quickBooksConnectionsService.getSanitizedErrorMessage(
          error,
          'The complete QuickBooks Customer catalog could not be loaded.',
        );
        return this.loadStatus(true);
      })
      .finally(() => {
        this.customerCatalogLoading = false;
      });
  }

  vendorHintFor(candidate: WallitSupplierCandidate): QuickBooksPartyHint | null {
    return this.vendorCatalog?.hints.find(hint => hint.wallitSourceId === candidate.sourceId) || null;
  }

  customerHintFor(candidate: WallitCompanyCandidate): QuickBooksPartyHint | null {
    return this.customerCatalog?.hints.find(hint => hint.wallitSourceId === candidate.sourceId) || null;
  }

  vendorsFor(hint: QuickBooksPartyHint): QuickBooksVendor[] {
    if (!this.vendorCatalog) {
      return [];
    }
    const ids = new Set(hint.quickBooksEntityIds);
    return this.vendorCatalog.vendors.filter(vendor => ids.has(vendor.id));
  }

  customersFor(hint: QuickBooksPartyHint): QuickBooksCustomer[] {
    if (!this.customerCatalog) {
      return [];
    }
    const ids = new Set(hint.quickBooksEntityIds);
    return this.customerCatalog.customers.filter(customer => ids.has(customer.id));
  }

  hasPossibleRoleOverlap(displayName: string): boolean {
    if (!this.vendorCatalog || !this.customerCatalog) {
      return false;
    }
    const comparableName = this.normalizePartyName(displayName);
    return this.vendorCatalog.vendors.some(vendor => this.normalizePartyName(vendor.displayName) === comparableName)
      && this.customerCatalog.customers.some(customer => this.normalizePartyName(customer.displayName) === comparableName);
  }

  hintFor(account: WallitAccountCandidate): QuickBooksAccountHint | null {
    return this.accountCatalog?.hints.find(hint => hint.wallitAccountId === account.id) || null;
  }

  quickBooksAccountsFor(hint: QuickBooksAccountHint): QuickBooksAccount[] {
    if (!this.accountCatalog) {
      return [];
    }
    const ids = new Set(hint.quickBooksAccountIds);
    return this.accountCatalog.accounts.filter(account => ids.has(account.id));
  }

  currencyDisplay(account: QuickBooksAccount): string {
    if (!account.currencyRef) {
      return 'Unknown';
    }
    return account.currencyRef.name
      ? `${account.currencyRef.value} — ${account.currencyRef.name}`
      : account.currencyRef.value;
  }

  buildQuickBooksAccountRows(accounts: QuickBooksAccount[]): QuickBooksAccountRow[] {
    const accountsById = new Map(accounts.map(account => [account.id, account]));
    const childrenByParentId = new Map<string, QuickBooksAccount[]>();
    const roots: QuickBooksAccount[] = [];
    const compareAccounts = (left: QuickBooksAccount, right: QuickBooksAccount): number =>
      left.name.localeCompare(right.name)
      || left.fullyQualifiedName.localeCompare(right.fullyQualifiedName)
      || left.id.localeCompare(right.id);

    accounts.forEach(account => {
      const parentId = account.parentRef?.id;
      if (!parentId || parentId === account.id || !accountsById.has(parentId)) {
        roots.push(account);
        return;
      }
      const siblings = childrenByParentId.get(parentId) || [];
      siblings.push(account);
      childrenByParentId.set(parentId, siblings);
    });

    roots.sort(compareAccounts);
    childrenByParentId.forEach(children => children.sort(compareAccounts));

    const rows: QuickBooksAccountRow[] = [];
    const visited = new Set<string>();
    const appendAccount = (account: QuickBooksAccount, depth: number): void => {
      if (visited.has(account.id)) {
        return;
      }
      visited.add(account.id);
      rows.push({account, depth});
      (childrenByParentId.get(account.id) || []).forEach(child => appendAccount(child, depth + 1));
    };

    roots.forEach(account => appendAccount(account, 0));
    accounts.slice().sort(compareAccounts).forEach(account => appendAccount(account, 0));
    return rows;
  }

  private buildWallitAccountGroups(accounts: WallitAccountCandidate[]): WallitAccountGroup[] {
    const groupDefinitions: Array<{
      accountTypeCode: WallitAccountCandidate['accountTypeCode'];
      label: string;
    }> = [
      {accountTypeCode: 'assets', label: 'Assets'},
      {accountTypeCode: 'expenses', label: 'Expenses'},
      {accountTypeCode: 'liabilities', label: 'Liabilities'},
      {accountTypeCode: 'revenue', label: 'Revenue'},
    ];
    return groupDefinitions
      .map(group => ({
        ...group,
        accounts: accounts
          .filter(account => account.accountTypeCode === group.accountTypeCode)
          .sort((left, right) => left.code.localeCompare(right.code) || left.id.localeCompare(right.id)),
      }))
      .filter(group => group.accounts.length > 0);
  }

  connect(): void {
    if (!this.status.actions.canConnect || this.starting) {
      return;
    }
    this.starting = true;
    this.quickBooksConnectionsService.startAuthorization()
      .then(result => {
        const authorizationUrl = new URL(result.authorizationUrl);
        if (authorizationUrl.origin !== 'https://appcenter.intuit.com') {
          throw new Error('Unexpected authorization destination');
        }
        window.location.assign(authorizationUrl.toString());
      })
      .catch(error => {
        this.starting = false;
        this.snackBar.open(
          this.quickBooksConnectionsService.getSanitizedErrorMessage(error, 'Unable to start the QuickBooks connection.'),
          'Close',
          {duration: 6000},
        );
        void this.loadStatus();
      });
  }

  remove(): void {
    if (!this.status.actions.canRemove || this.removing) {
      return;
    }
    void this.uiAlertService.presentAlertConfirm(
      'Remove this QuickBooks connection? Wallit will stop using it immediately and attempt token revocation once.',
    ).then(confirmed => {
      if (!confirmed) {
        return;
      }
      this.removing = true;
      this.quickBooksConnectionsService.removeConnection()
        .then(status => {
          this.status = status;
          this.accountCatalog = null;
          this.quickBooksAccountRows = [];
          this.vendorCatalog = null;
          this.customerCatalog = null;
        })
        .catch(error => {
          this.snackBar.open(
            this.quickBooksConnectionsService.getSanitizedErrorMessage(error, 'Unable to remove the QuickBooks connection.'),
            'Close',
            {duration: 6000},
          );
          void this.loadStatus();
        })
        .finally(() => {
          this.removing = false;
        });
    });
  }

  retryCleanup(): void {
    if (!this.status.actions.canRetryCleanup || this.cleaningUp) {
      return;
    }
    this.cleaningUp = true;
    this.quickBooksConnectionsService.retryCleanup()
      .then(status => {
        this.status = status;
      })
      .catch(error => {
        this.snackBar.open(
          this.quickBooksConnectionsService.getSanitizedErrorMessage(error, 'QuickBooks cleanup could not be confirmed.'),
          'Close',
          {duration: 6000},
        );
        void this.loadStatus();
      })
      .finally(() => {
        this.cleaningUp = false;
      });
  }

  private unavailableStatus(): QuickBooksConnectionStatus {
    return {
      actions: {canConnect: false, canRemove: false, canRetryCleanup: false},
      cleanupRequired: false,
      company: null,
      configurationAvailable: false,
      provider: 'QUICKBOOKS_ONLINE',
      state: 'CONFIGURATION_UNAVAILABLE',
    };
  }

  private normalizePartyName(value: string): string {
    return String(value || '').normalize('NFKC').trim().toLowerCase().replace(/\s+/g, ' ');
  }
}
